import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Dimensions,
  ImageStyle,
  Animated,
  ScrollView,
} from "react-native";
import Svg, { Path } from 'react-native-svg';
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  PepsiCoLogo,
  WarehouseIcon,
  LocationIcon,
  NotificationIcon,
  DropdownIcon,
} from '../assets/SVG-Icons';
import ProfileDropdown from './ProfileDropdown';
import MobileSidepanel from './MobileSidepanel';
import NotificationOverlay from './NotificationOverlay';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import LocationModal from './LocationModal';
import { DLODropdownOption } from '../types/dlo.types';
import { setSelectedLocation } from '../../../store/slices/uiPreferencesSlice';
import { setSelectedLocation as setAppDataSelectedLocation } from '../../../store/slices/appDataSlice';
import { 
  fetchNotificationsStart, 
  fetchNotificationsSuccess, 
  fetchNotificationsFailure,
  markNotificationAsRead as markNotificationAsReadAction,
  markAllNotificationsAsRead as markAllNotificationsAsReadAction,
} from '../../../store/slices/notificationSlice';
import { 
  setSelectedTicketId,
  setTicketDetailSource,
} from '../../../store/slices/ticketingPortalSlice';
import { 
  fetchNotifications, 
  markNotificationAsRead as markNotificationAsReadAPI 
} from '../services/notificationService';
import { logger } from '../../../utils/logger';
import { APP_VERSION as APP_VERSION_ENV } from '@env';

// Web: process.env.APP_VERSION is injected by webpack DefinePlugin
// Mobile: APP_VERSION_ENV is read from .env via react-native-dotenv (@env)
const APP_VERSION = process.env.APP_VERSION ? process.env.APP_VERSION : (APP_VERSION_ENV || '2.4.0');

interface DLOHeaderProps {
  activeScreen?: string;
  onNavigate?: (screen: string, params?: { [key: string]: any }) => void;
}

const DLOHeaderComponent: React.FC<DLOHeaderProps> = ({ activeScreen = 'Home', onNavigate = () => {} }) => {
  const insets = useSafeAreaInsets();
  const [isMobile, setIsMobile] = React.useState(false);
  const [isTablet, setIsTablet] = React.useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = React.useState(false);
  const [isMobileSidepanelOpen, setIsMobileSidepanelOpen] = React.useState(false);
  const [isNotificationOverlayVisible, setIsNotificationOverlayVisible] = React.useState(false);
  const rotationAnim = React.useRef(new Animated.Value(0)).current;
  const dispatch = useDispatch();

  // Get authentication state from Redux
  const authState = useSelector((state: RootState) => state.auth);
  const user = authState.user;

  // Get notifications state from Redux
  const notificationState = useSelector((state: RootState) => state.notifications);
  const { notifications, unreadCount, loading: notificationsLoading } = notificationState;

  // Get app data from Redux
  const appData = useSelector((state: RootState) => state.appData);
  const periodWeekData = appData.periodWeek?.data;
  const selectedLocation = appData.selectedLocation;

  // Location modal state and options
  const [isLocationModalVisible, setIsLocationModalVisible] = React.useState(false);
  const locationOptions: DLODropdownOption[] = (appData.locations || []).map((loc: any) => ({
    id: String(loc.id || loc.locationId || loc.name),
    label: loc.name || loc.displayText,
    value: String(loc.id || loc.locationId || loc.name),
  }));

  const handleOpenLocationModal = () => setIsLocationModalVisible(true);
  const handleCloseLocationModal = () => setIsLocationModalVisible(false);
  const handleLocationSave = (newSelectedLocation: DLODropdownOption | null) => {
    if (newSelectedLocation) {
      const fullLocation = (appData.locations || []).find(
        (loc: any) => String(loc.id || loc.locationId || loc.name) === String(newSelectedLocation.id)
      );
      const locationObj = fullLocation ? {
        ...fullLocation,
        id: String(fullLocation.id),
        displayText: fullLocation.name || (fullLocation as any).displayText,
      } : {
        id: String(newSelectedLocation.id),
        displayText: newSelectedLocation.label,
      };
      dispatch(setSelectedLocation(locationObj));
      dispatch(setAppDataSelectedLocation(locationObj));
      setIsLocationModalVisible(false);
      // Navigate to DLO landing page after location change
      // if (onNavigate) {
      //  onNavigate('DLODashboard');
      // }
    }
  };

  // Function to fetch notifications
  const fetchUserNotifications = React.useCallback(async () => {
    try {
      // Get user GPID from auth state
      const userGpid = appData.userInfo?.GPID || authState.user?.claims?.employeeId || 
                       authState.user?.employeeId || 
                       '55555555'; // Fallback GPID
      
      dispatch(fetchNotificationsStart());
      const response = await fetchNotifications(userGpid);
      dispatch(fetchNotificationsSuccess(response.data));
    } catch (error) {
      logger.error('Error fetching notifications:', error);
      dispatch(fetchNotificationsFailure('Failed to fetch notifications'));
    }
  }, [authState.user, appData.userInfo, dispatch]);

  // Function to handle notification click
  const handleNotificationClick = () => {
    try {
      fetchUserNotifications();
    } catch (err) {
      logger.error('Error fetching notifications on overlay open:', err);
    }

    setIsNotificationOverlayVisible(true);
  };

  // Get user GPID
  const userGpid = appData.userInfo?.GPID || authState.user?.claims?.employeeId || 
                   authState.user?.employeeId || 
                   '55555555'; // Fallback GPID

  // Function to handle mark as read
  const handleMarkAsRead = async (notificationId: number, userGpid: string) => {
    try {
      // Optimistically update UI immediately
      dispatch(markNotificationAsReadAction(notificationId));

      // Fire API request in background
      await markNotificationAsReadAPI(notificationId, userGpid);
      logger.log(`Notification ${notificationId} marked as read`);
    } catch (error) {
      logger.error('Error marking notification as read:', error);
      // Re-sync with server on failure to ensure correct state
      try {
        await fetchUserNotifications();
      } catch (e) {
        logger.error('Failed to refetch notifications after mark-as-read error:', e);
      }
    }
  };

  // // Function to handle mark all as read
  const handleMarkAllAsRead = async (userGpid: string) => {
    try {
      // Optimistically mark all as read locally
      dispatch(markAllNotificationsAsReadAction());

      const { markAllNotificationsAsRead } = await import('../services/notificationService');
      await markAllNotificationsAsRead(userGpid);
      logger.log('All notifications marked as read');
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      // Re-sync with server on failure
      try {
        await fetchUserNotifications();
      } catch (e) {
        logger.error('Failed to refetch notifications after mark-all error:', e);
      }
    }
  };

  // // Function to handle dismiss all
  const handleDismissAll = () => {
    const { clearNotifications } = require('../../../store/slices/notificationSlice');
    dispatch(clearNotifications());
    setIsNotificationOverlayVisible(false);
    logger.log('All notifications dismissed');

    // Start a background refetch to sync server state (will be filtered by lastDismissTime)
    fetchUserNotifications();
  };

  // Function to handle Redux dispatch actions from NotificationOverlay
  const handleDispatchAction = (action: { type: string; payload: any }) => {
    switch (action.type) {
      case 'ticketing/setSelectedTicketId':
        dispatch(setSelectedTicketId(action.payload));
        break;
      case 'ticketing/setTicketDetailSource':
        dispatch(setTicketDetailSource(action.payload));
        break;
      default:
        logger.warn(`Unknown dispatch action type: ${action.type}`);
    }
  };

  // Effect for fetching notifications on mount and periodically
  React.useEffect(() => {
    // Fetch notifications immediately on mount
    fetchUserNotifications();

    // Set up polling every 60 seconds (1 minute)
    const intervalId = setInterval(() => {
      fetchUserNotifications();
    }, 60000); // 60000ms = 1 minute

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchUserNotifications]);

  React.useEffect(() => {
    const updateLayout = () => {
      const { width } = Dimensions.get('window');
      const isMobileDevice = width < 768;
      const isTabletDevice = width >= 768 && width <= 1024;
      setIsMobile(isMobileDevice || isTabletDevice);
      setIsTablet(isTabletDevice);
    };

    // Initial check
    updateLayout();

    // Add event listener for orientation/dimension changes
    const subscription = Dimensions.addEventListener('change', updateLayout);

    // Add scroll listener to hide dropdown when page scrolls
    const handleScroll = () => {
      if (isDropdownVisible) {
        setIsDropdownVisible(false);
        // Reset rotation animation
        Animated.timing(rotationAnim, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }).start();
      }
    };

    // For web, add scroll listener to window
    if (Platform.OS === 'web') {
      window.addEventListener('scroll', handleScroll, true); // Use capture phase
    }

    // Cleanup
    return () => {
      subscription.remove();
      if (Platform.OS === 'web') {
        window.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [isDropdownVisible, rotationAnim]);

  const handleProfilePress = () => {
    const newVisibility = !isDropdownVisible;
    setIsDropdownVisible(newVisibility);
    
    // Animate rotation
    Animated.timing(rotationAnim, {
      toValue: newVisibility ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  const handleCloseDropdown = () => {
    setIsDropdownVisible(false);
    
    // Animate rotation back to original position
    Animated.timing(rotationAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const userName = authState.user?.claims?.name || authState.user?.name;
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Format period week display
  const getPeriodWeekDisplay = () => {
    if (periodWeekData?.period && periodWeekData?.week) {
      return `P${periodWeekData.period}W${periodWeekData.week}`;
    }
    return 'P7W1'; // Fallback value
  };

  // Get location display text
  const getLocationDisplayText = () => {
    return selectedLocation?.displayText || selectedLocation?.name || 'Phoenix, AZ'; // Fallback value
  };

  // Prepare user info for dropdown
  const userInfo = {
    name: authState.user?.claims?.name || authState.user?.name || 'Guest',
    id: authState.user?.idTokenClaims?.iat || 'No ID',
    avatar: user?.avatar || user?.picture
  };

  // Map selectedLocation from appData to DLODropdownOption (if present)
  const selectedLocationOption: DLODropdownOption | null = selectedLocation
    ? { 
        id: String(selectedLocation.id || selectedLocation.locationId), 
        label: selectedLocation.displayText || selectedLocation.name, 
        value: String(selectedLocation.id || selectedLocation.locationId) 
      }
    : null;

  return (
    <>
    {Platform.OS === "ios" && insets.top > 0 && (
      <View
        style={{
          height: insets.top,
          backgroundColor: "#272727",
          width: "100%",
        }}
      />
    )}
    {/* Plain View, not SafeAreaView: react-native core SafeAreaView shrinks its own
        frame by the native safe-area insets on iOS, which — combined with the
        manual useSafeAreaInsets() padding below — clipped the header's right edge
        in landscape. Insets are already applied manually via insets.top/left/right.
        The header itself is absolutely positioned on mobile (floats over body
        content, which reserves matching space via App.tsx's marginTop), so this
        wrapper intentionally reserves no extra flow height of its own. */}
    <View style={styles.safeArea}>
      <View style={[
        styles.container, 
        !isMobile ? styles.desktopHeader : styles.mobileHeader,
        isMobile && styles.mobileFixed // Only apply fixed positioning on mobile
      ]}>
        {!isMobile ? (
          // Desktop/Tablet Header Layout
          <>
            {/* Left section - Logo, Separator, Period */}
            <View style={styles.leftSection}>
              {/* Logo */}
              <PepsiCoLogo style={styles.logo} />
              {/* Separator and Period Week - Hidden on tablet */}
              {!isTablet && (
                <>
                  <View style={styles.separator} />
                  <View style={styles.periodContainer}>
                    <Text style={styles.periodText}>{getPeriodWeekDisplay()}</Text>
                    <Text style={styles.periodVersionText}>v{APP_VERSION}</Text>
                  </View>
                </>
              )}
            </View>

            {/* Center section - Site Name with Icon */}
            <View style={styles.centerSection}>
              <Image
                source={require('../../../assets/warehouse_navigator.png')}
                style={styles.siteIcon as ImageStyle}
                resizeMode="contain"
              />
              <View>
                <Text style={styles.siteTitle}>Warehouse Navigator</Text>
              </View>
            </View>

            {/* Right section */}
            <View style={styles.rightSection}>
              {/* Location Info - Hidden on tablet */}
              {!isTablet && (
                <View style={styles.locationInfo}>
                  <View style={styles.locationTypeContainer}>
                    <WarehouseIcon fill="#EFEFEF" width={12} height={12} />
                    <Text style={styles.locationType}>Warehouse</Text>
                  </View>
                  <TouchableOpacity
                    style={styles.locationNameContainer}
                    onPress={handleOpenLocationModal}
                    accessibilityRole="button"
                  >
                    <LocationIcon fill="#EFEFEF" width={10} height={12} />
                    <Text style={styles.locationName}>{getLocationDisplayText()}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* Notification */}
               <TouchableOpacity 
                style={styles.notificationContainer}
                onPress={handleNotificationClick}
                accessibilityRole="button"
                accessibilityLabel={`Notifications, ${unreadCount} unread`}
              >
                <View style={styles.notificationIconContainer}>
                  <NotificationIcon fill="#EFEFEF" width={24} height={24} />
                  {unreadCount > 0 && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>

              {/* User Profile */}
              <TouchableOpacity style={styles.profileContainer} onPress={handleProfilePress}>
                <View style={styles.avatarContainer}>
                  <Text style={styles.avatarText}>{getUserInitials()}</Text>
                </View>
                <Animated.View
                  style={{
                    transform: [{
                      rotate: rotationAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '180deg'],
                      }),
                    }],
                  }}
                >
                  <DropdownIcon style={styles.dropdownIcon} fill="#EFEFEF" width={24} height={24} />
                </Animated.View>
              </TouchableOpacity>
            </View>
          </>
        ) : (
          // Mobile Header Layout
          <>
            {/* First Row: Menu, Logo, Avatar */}
            <View style={[
              styles.mobileFirstRow,
              isTablet && { height: 48 },
              { paddingLeft: 16 + insets.left, paddingRight: 16 + insets.right },
            ]}>
              {/* Hamburger Menu Icon - Left */}
              <TouchableOpacity 
                style={styles.mobileMenuButton}
                onPress={() => {
                  console.log('Hamburger clicked, current state:', isMobileSidepanelOpen);
                  setIsMobileSidepanelOpen(!isMobileSidepanelOpen);
                }}
              >
                {isMobileSidepanelOpen ? (
                  // Close Icon
                  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                    <Path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#EFEFEF"/>
                  </Svg>
                ) : (
                  // Hamburger Icon
                  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                    <Path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="#EFEFEF"/>
                  </Svg>
                )}
              </TouchableOpacity>

              {/* Warehouse Navigator Title - Center */}
              <View style={styles.mobileLogoContainer}>
                <Image
                  source={require('../../../assets/warehouse_navigator.png')}
                  style={styles.mobileSiteIcon as ImageStyle}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.mobileSiteTitle}>Warehouse Navigator</Text>
                </View>
              </View>

              {/* Right group: Notification Bell + Avatar */}
              <View style={styles.mobileRightGroup}>
                <TouchableOpacity
                  style={styles.mobileNotificationButton}
                  onPress={handleNotificationClick}
                  accessibilityRole="button"
                  accessibilityLabel={`Notifications, ${unreadCount} unread`}
                >
                  <View style={styles.notificationIconContainer}>
                    <NotificationIcon fill="#EFEFEF" width={24} height={24} />
                    {unreadCount > 0 && (
                      <View style={styles.badge}>
                        <Text style={styles.badgeText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>

                {/* User Avatar */}
                <TouchableOpacity style={styles.mobileAvatarButton} onPress={handleProfilePress}>
                  <View style={styles.mobileAvatarContainer}>
                    <Text style={styles.avatarText}>{getUserInitials()}</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>

            {/* Second Row: Period/Week and Location Info */}
            <View style={styles.mobileSecondRow}>
              {/* Top Divider - Full Width */}
              <View style={styles.mobileTopDivider} />
              
              {/* Content Container with padding */}
              <View style={[
                styles.mobileSecondRowContent,
                { paddingLeft: 16 + insets.left, paddingRight: 16 + insets.right },
              ]}>
                {/* Period Week - Left */}
                <View style={styles.mobilePeriodContainer}>
                  <Text style={styles.mobilePeriodText}>{getPeriodWeekDisplay()}</Text>
                  <Text style={styles.mobilePeriodVersionText}>v{APP_VERSION}</Text>
                </View>

                {/* Location Info - Right (stacked vertically) */}
                <View style={styles.mobileLocationStack}>
                  {/* Location Type */}
                  <View style={styles.mobileLocationTypeRow}>
                    <WarehouseIcon fill="#EFEFEF" width={14} height={14} />
                    <Text style={styles.mobileLocationType}>Warehouse</Text>
                  </View>
                  
                  {/* Location Name */}
                  <TouchableOpacity
                    style={styles.mobileLocationNameRow}
                    onPress={handleOpenLocationModal}
                    accessibilityRole="button"
                  >
                    <LocationIcon fill="#EFEFEF" width={14} height={14} />
                    <Text style={styles.mobileLocationName}>{getLocationDisplayText()}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </>
        )}
        
        {/* Profile Dropdown */}
        <ProfileDropdown 
          isVisible={isDropdownVisible}
          onClose={handleCloseDropdown}
          position={{ top: isMobile && !isTablet ? insets.top + 56 : (isTablet ? 48 : 56), right: !isMobile ? 40 : 16 }}
          userInfo={userInfo}
        />
      </View>
    </View>

    {/* Mobile Sidepanel - Outside SafeAreaView for proper positioning */}
    {isMobile && (
      <>
        {console.log('Rendering MobileSidepanel, isMobile:', isMobile, 'isOpen:', isMobileSidepanelOpen)}
        <MobileSidepanel
          isVisible={isMobileSidepanelOpen}
          onClose={() => setIsMobileSidepanelOpen(false)}
          activeScreen={activeScreen}
          onNavigate={onNavigate}
        />
      </>
    )}
    {/* Location Modal (shared) */}
    <LocationModal
      visible={isLocationModalVisible}
      onClose={handleCloseLocationModal}
      selectedLocation={selectedLocationOption}
      locationOptions={locationOptions}
      onSave={handleLocationSave}
      title="Select Location"
      titleAlign="left"
    />
    
    {/* Notification Overlay */}
    <NotificationOverlay
      visible={isNotificationOverlayVisible}
      onClose={() => setIsNotificationOverlayVisible(false)}
      notifications={notifications}
      onMarkAsRead={handleMarkAsRead}
      onMarkAllAsRead={handleMarkAllAsRead}
      onDismissAll={handleDismissAll}
      userGpid={userGpid}
      loading={notificationsLoading}
      onNavigate={onNavigate}
      onDispatchAction={handleDispatchAction}
    />
    </>
  );
};

const HeaderSpacer = () => {
  const { width } = Dimensions.get('window');
  const isMobile = width < 768;
  const isTablet = width >= 768 && width <= 1024;

  let spacerHeight = 0;
  if (isTablet) {
    const topRowHeight = 48;
    const bottomRowHeight = 54;
    spacerHeight = topRowHeight + bottomRowHeight;
  }

  return isTablet ? <View style={{ height: spacerHeight }} /> : null;
}

const DLOHeader : React.FC<DLOHeaderProps> = (props) => (
  <>
    <DLOHeaderComponent {...props} />
    <HeaderSpacer />
  </>
);

const styles = StyleSheet.create({
  safeArea: {
    backgroundColor: '#272727',
    position: 'relative',
    zIndex: 1000,
  },
  container: {
    backgroundColor: '#272727',
    borderBottomWidth: 1,
    borderBottomColor: '#3A3A3A',
  },
  // left/right: 0 alone define full width here — an explicit width would
  // conflict with them on web and can clip the header on the right in landscape.
  mobileFixed: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  desktopHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    height: 64,
    width: '100%',
  },
  mobileHeader: {
    flexDirection: 'column',
    width: '100%',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  logo: {
    width: 100,
    height: 24.347,
    marginTop: -4,
  },
  separator: {
    width: 1,
    height: 32,
    backgroundColor: '#3A3A3A',
  },
  periodText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    marginTop: 3,
    height: 22,
    width: 'auto',
    overflow: 'hidden',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    flexGrow: 0,
  },
  periodContainer: {
    justifyContent: 'center',
    gap: 0,
    marginTop: -2,
  },
  periodVersionText: {
    color: '#EFEFEF',
    fontSize: 11,
    fontWeight: '400',
    lineHeight: 14,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  centerSection: {
    position: 'absolute',
    left: '43%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    top: '50%',
    marginTop: -20,
    marginLeft: -50,
  },
  siteIcon: {
    width: 40,
    height: 40,
    marginRight: 8,
  },
  siteTitle: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  locationInfo: {
    gap: 2,
    justifyContent: 'center',
  },
  locationTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  locationType: {
    color: '#EFEFEF',
    fontSize: 12,
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  locationNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  locationName: {
    color: '#EFEFEF',
    fontSize: 12,
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    marginLeft: 3,
    marginTop: 0,
    marginBottom: 0,
    marginRight: 0,
  },
  notificationContainer: {
    width: 56,
    height: 64,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  notificationIconContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    right: -3,
    top: 0,
    backgroundColor: '#D40020',
    borderRadius: 99,
    width: 'auto',
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 14,
    textTransform: 'uppercase',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  profileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
  },
  avatarContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#818181',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  dropdownIcon: {
    width: 24,
    height: 24,
  },
  mobileLogo: {
    width: 100,
    height: 24.347,
    flexShrink: 0,
    marginTop: -4,
  },
  mobileTitle: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  mobileProfile: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  mobileAvatarContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#818181',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileBadge: {
    position: 'absolute',
    right: -9,
    top: 12,
    backgroundColor: '#D40020',
    borderRadius: 99,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  // New mobile header styles for Figma design
  mobileFirstRow: {
    height: 64,
    backgroundColor: '#272727',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  mobileMenuButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  mobileLogoContainer: {
    position: 'absolute',
    left: '50%',
    transform: [{ translateX: -90 }],
    flexDirection: 'row',
    alignItems: 'center',
  },
  mobileAvatarButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#818181',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileRightGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobileNotificationButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  mobileSiteIcon: {
    width: 32,
    height: 32,
    marginRight: 8,
  },
  mobileSiteTitle: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  mobileSecondRow: {
    height: 54,
    backgroundColor: '#272727',
    position: 'relative',
    width: '100%',
    zIndex: 997
  },
  mobileTopDivider: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: '#3A3A3A',
    width: '100%',
  },
  mobileSecondRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
    paddingHorizontal: 16
  },
  mobilePeriodText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
    }),
  },
  mobilePeriodContainer: {
    justifyContent: 'center',
    gap: 0,
    marginTop: -1,
  },
  mobilePeriodVersionText: {
    color: '#EFEFEF',
    fontSize: 10,
    fontWeight: '400',
    lineHeight: 14,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  mobileLocationStack: {
    gap: 2,
    alignItems: 'flex-start',
  },
  mobileLocationTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mobileLocationType: {
    color: '#EFEFEF',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  mobileLocationNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mobileLocationName: {
    color: '#EFEFEF',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
});

export default DLOHeader;
